import { axiosAmApiInstance as amApi } from '../utils/axios';
import qs from 'qs';
import { Organisation } from '../@types/organization';

export enum OidcGrantType {
  PasswordCredentials = 'password',
  TenantChange = 'tenant_change',
  ExternalLogin = 'external_login',
  MfaVerification = 'mfa_verification',
  RefreshToken = 'refresh_token'
}

type PasswordCredentialsRequest = {
  grant_type: OidcGrantType.PasswordCredentials;
  username: string;
  password: string;
  scope: string;
  mfa_code?: string;
};

type TenantChangeRequest = {
  grant_type: OidcGrantType.TenantChange;
  tenant_id: string;
  scope: string;
};

type ExternalLoginRequest = {
  grant_type: OidcGrantType.ExternalLogin;
  id_token: string;
  provider: string;
  scope: string;
};

type MfaVerificationRequest = {
  grant_type: OidcGrantType.MfaVerification;
  mfa_code: string;
  scope: string;
};

type RefreshTokenRequest = {
  grant_type: OidcGrantType.RefreshToken;
  refresh_token: string;
};

type TokenRequestBody =
  | PasswordCredentialsRequest
  | TenantChangeRequest
  | ExternalLoginRequest
  | MfaVerificationRequest
  | RefreshTokenRequest;

export type TokenResponse = {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
};

export type ProvisionalLoginResult = {
  provisionalToken: string;
  status?: string | number;
  redirectUrl?: string;
};

export type TenantLoginResult = {
  tokenResponse: TokenResponse;
  status?: string | number;
  redirectUrl?: string;
};

export type MfaRequiredStatus = 'MfaCodeRequired' | 'MfaSetupRequired';

export class MfaRequiredError extends Error {
  readonly status: MfaRequiredStatus;

  constructor(status: MfaRequiredStatus) {
    super();
    this.name = 'MfaRequiredError';
    this.status = status;
  }
}

const TOKEN_ENDPOINT = '/connect/token';
const TENANTS_ENDPOINT = '/tenants/access';
const MFA_SETUP_ENDPOINT = '/authorization/mfa/setup';
const MFA_VERIFY_ENDPOINT = '/authorization/mfa/verify';
const MFA_DISABLE_ENDPOINT = '/authorization/mfa/disable';
const DEFAULT_SCOPE = 'offline_access';

const REFRESH_TOKEN_KEY = 'refreshToken';
const PROVISIONAL_TOKEN_KEY = 'provisionalToken';

export const setRefreshToken = (token: string | null) => {
  if (token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const setProvisionalToken = (token: string | null) => {
  if (token) {
    localStorage.setItem(PROVISIONAL_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(PROVISIONAL_TOKEN_KEY);
  }
};

export const getProvisionalToken = (): string | null => {
  return localStorage.getItem(PROVISIONAL_TOKEN_KEY);
};

const callTokenEndpoint = async (
  body: TokenRequestBody
): Promise<TokenResponse & { status?: string | number; redirectUrl?: string }> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/x-www-form-urlencoded'
  };

  const response = await amApi
    .post(`${TOKEN_ENDPOINT}`, qs.stringify(body), {
      headers
    })
    .catch((error) => {
      if (
        error.type === 'Validation.User' &&
        error.detail ===
          'Multi-factor authentication is enforced on this tenant but has not been set up on your account.'
      ) {
        throw new MfaRequiredError('MfaSetupRequired');
      }
      throw error;
    });
  return response.data;
};

export const loginWithPassword = async (
  username: string,
  password: string,
  mfaCode?: string
): Promise<ProvisionalLoginResult> => {
  const body: PasswordCredentialsRequest = {
    grant_type: OidcGrantType.PasswordCredentials,
    username,
    password,
    scope: DEFAULT_SCOPE,
    ...(mfaCode ? { mfa_code: mfaCode } : {})
  };

  const data = await callTokenEndpoint(body);

  return {
    provisionalToken: data.access_token,
    status: data.status,
    redirectUrl: data.redirectUrl
  };
};

export const loginWithExternalToken = async (idToken: string): Promise<ProvisionalLoginResult> => {
  const body: ExternalLoginRequest = {
    grant_type: OidcGrantType.ExternalLogin,
    id_token: idToken,
    provider: 'microsoft',
    scope: DEFAULT_SCOPE
  };

  const data = await callTokenEndpoint(body);

  const provisionalToken = data.access_token;
  setProvisionalToken(provisionalToken);

  return {
    provisionalToken,
    status: data.status
  };
};

export const loginWithMfaVerification = async (
  mfaCode: string
): Promise<ProvisionalLoginResult> => {
  const body: MfaVerificationRequest = {
    grant_type: OidcGrantType.MfaVerification,
    mfa_code: mfaCode,
    scope: DEFAULT_SCOPE
  };

  const data = await callTokenEndpoint(body);

  const newProvisionalToken = data.access_token;
  setProvisionalToken(newProvisionalToken);

  return {
    provisionalToken: newProvisionalToken,
    status: data.status
  };
};

export const fetchAccessibleTenants = async (): Promise<Organisation[]> => {
  const response = await amApi.get(TENANTS_ENDPOINT);

  const { accessibleTenants } = response.data as {
    accessibleTenants: { id: string; name: string }[];
  };

  return accessibleTenants.map(
    (tenant: any): Organisation => ({
      organizationId: tenant.id,
      organizationName: tenant.name,
      displayName: tenant.displayName,
      active: true
    })
  );
};

export const loginWithTenantChange = async (tenantId: string): Promise<TenantLoginResult> => {
  const body: TenantChangeRequest = {
    grant_type: OidcGrantType.TenantChange,
    tenant_id: tenantId,
    scope: DEFAULT_SCOPE
  };

  const data = await callTokenEndpoint(body);

  const result: TenantLoginResult = {
    tokenResponse: {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      token_type: data.token_type,
      expires_in: data.expires_in
    },
    status: data.status,
    redirectUrl: data.redirectUrl
  };

  if (result.tokenResponse.refresh_token) {
    setRefreshToken(result.tokenResponse.refresh_token);
  }

  setProvisionalToken(null);

  return result;
};

const requestRefreshedAccessToken = async (): Promise<TokenResponse | null> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    const body: RefreshTokenRequest = {
      grant_type: OidcGrantType.RefreshToken,
      refresh_token: refreshToken
    };

    const data = await callTokenEndpoint(body);

    const tokenResponse: TokenResponse = {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      token_type: data.token_type,
      expires_in: data.expires_in
    };

    if (tokenResponse.refresh_token) {
      setRefreshToken(tokenResponse.refresh_token);
    }

    return tokenResponse;
  } catch {
    setRefreshToken(null);
    return null;
  }
};

let pendingRefresh: Promise<TokenResponse | null> | null = null;

export const refreshAccessToken = (): Promise<TokenResponse | null> => {
  pendingRefresh ??= requestRefreshedAccessToken().finally(() => {
    pendingRefresh = null;
  });
  return pendingRefresh;
};

export const clearTokens = () => {
  setRefreshToken(null);
  setProvisionalToken(null);
};

export const mfaSetup = async (phoneNumber: string): Promise<void> => {
  await amApi.post(MFA_SETUP_ENDPOINT, { phoneNumber });
};

export const mfaDisable = async (): Promise<void> => {
  await amApi.post(MFA_DISABLE_ENDPOINT);
};

export const mfaVerify = async (code: string): Promise<void> => {
  await amApi.post(MFA_VERIFY_ENDPOINT, { code });
};

type AmUserResponse = {
  id: string;
  email: string;
  name?: string;
  phoneNumber?: string;
  anonymized: boolean;
  address?: string;
  city?: string;
  zipCode?: string;
  isMfaEnabled: boolean;
};

export const fetchCurrentUser = async (): Promise<Record<string, any>> => {
  const response = await amApi.get(`/users/current`);

  const data = response.data as AmUserResponse;

  return {
    userId: data.id,
    id: data.id,
    email: data.email,
    userName: data.email,
    name: data.name ?? '',
    phoneNumber: data.phoneNumber ?? '',
    phoneNumberConfirmed: !!data.phoneNumber,
    anonymized: data.anonymized,
    address: data.address ?? '',
    city: data.city ?? '',
    zipCode: data.zipCode ?? '',
    multiFactorEnabled: data.isMfaEnabled
  };
};
